import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabaseClient';

export interface PresencePayload {
  collaborator_id: string;
  full_name: string;
  avatar_url: string | null;
  context_type: 'dossier' | 'route';
  context_id: string | null;
  context_label: string;
  /** ISO timestamp posé au moment du track. Sert au merge multi-tabs. */
  online_at: string;
}

/** État brut renvoyé par channel.presenceState() : ref aléatoire → payloads. */
export type PresenceState = Record<string, PresencePayload[]>;

const PRESENCE_CHANNEL_NAME = 'presence:cabinet';
/** Délai avant tentative de reconnexion après fermeture. */
const RECONNECT_DELAY_MS = 2000;

/**
 * Dédup par collaborator_id (multi-tabs : on garde le payload le plus récent),
 * filtre l'utilisateur courant si fourni, trie par nom alphabétique.
 *
 * Fonction pure → testable sans Supabase.
 */
export function mergePresenceState(
  state: PresenceState,
  currentCollaboratorId: string | null,
): PresencePayload[] {
  const latestByCollaborator = new Map<string, PresencePayload>();
  for (const payloads of Object.values(state)) {
    for (const p of payloads) {
      const existing = latestByCollaborator.get(p.collaborator_id);
      if (!existing || p.online_at > existing.online_at) {
        latestByCollaborator.set(p.collaborator_id, p);
      }
    }
  }
  let list = Array.from(latestByCollaborator.values());
  if (currentCollaboratorId) {
    list = list.filter((p) => p.collaborator_id !== currentCollaboratorId);
  }
  list.sort((a, b) => a.full_name.localeCompare(b.full_name, 'fr'));
  return list;
}

// ============================================================
// Singleton channel : un seul canal Realtime par topic.
// Auto-reconnect en cas de CLOSED / CHANNEL_ERROR / TIMED_OUT
// (le JWT auth Supabase expire après ~1h, ce qui ferme silencieusement
// le canal Realtime — sans reconnexion, plus aucun event après).
// ============================================================

type StateListener = (state: PresenceState) => void;

interface PresenceSingleton {
  channel: RealtimeChannel;
  selfKey: string;
  current: PresencePayload;
  listeners: Set<StateListener>;
  /** true = leave() a été appelé volontairement, on ne tente plus de reconnect */
  stopped: boolean;
}

let singleton: PresenceSingleton | null = null;

function notifyListeners() {
  if (!singleton) return;
  const state = singleton.channel.presenceState() as PresenceState;
  for (const fn of singleton.listeners) fn(state);
}

function buildChannel(s: PresenceSingleton): RealtimeChannel {
  const channel = supabase.channel(PRESENCE_CHANNEL_NAME, {
    config: { presence: { key: s.selfKey } },
  });

  // Toutes les callbacks AVANT subscribe
  channel
    .on('presence', { event: 'sync' }, notifyListeners)
    .on('presence', { event: 'join' }, notifyListeners)
    .on('presence', { event: 'leave' }, notifyListeners);

  channel.subscribe(async (status) => {
    if (!singleton || singleton.stopped) return;

    if (status === 'SUBSCRIBED') {
      await channel.track(singleton.current);
      return;
    }

    // Connexion fermée (JWT expiré, réseau, etc.) → reconstruction du canal.
    // On évite la tempête de reconnexions avec un petit délai.
    if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      setTimeout(() => {
        if (!singleton || singleton.stopped) return;
        // Retirer l'ancien channel proprement avant d'en recréer un nouveau
        supabase.removeChannel(singleton.channel);
        singleton.channel = buildChannel(singleton);
      }, RECONNECT_DELAY_MS);
    }
  });

  return channel;
}

/**
 * Joint le canal présence cabinet (singleton) et fait un premier track.
 * Si déjà joint pour le même collaborateur, met juste à jour le payload courant.
 *
 * Retourne updatePayload + leave. Le canal lui-même n'est pas exposé.
 */
export function joinPresenceCabinet(initial: PresencePayload): {
  updatePayload: (next: PresencePayload) => Promise<void>;
  leave: () => void;
} {
  // Si déjà joint pour ce collaborateur, on met juste à jour le payload courant
  if (singleton && singleton.selfKey === initial.collaborator_id) {
    singleton.current = initial;
    singleton.stopped = false;
    void singleton.channel.track(initial);
    return {
      updatePayload: async (next) => {
        if (!singleton) return;
        singleton.current = next;
        await singleton.channel.track(next);
      },
      leave: leavePresenceCabinet,
    };
  }

  // Sinon, nettoyer le précédent s'il existe (changement d'utilisateur)
  if (singleton) {
    singleton.stopped = true;
    supabase.removeChannel(singleton.channel);
    singleton = null;
  }

  // Création du singleton — le channel sera fixé juste après par buildChannel
  singleton = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    channel: null as any, // assigné juste après
    selfKey: initial.collaborator_id,
    current: initial,
    listeners: new Set(),
    stopped: false,
  };
  singleton.channel = buildChannel(singleton);

  return {
    updatePayload: async (next) => {
      if (!singleton) return;
      singleton.current = next;
      await singleton.channel.track(next);
    },
    leave: leavePresenceCabinet,
  };
}

function leavePresenceCabinet() {
  if (!singleton) return;
  singleton.stopped = true;
  supabase.removeChannel(singleton.channel);
  singleton = null;
}

/**
 * S'abonner aux mises à jour de la liste de présence depuis l'UI.
 * Retourne une fonction unsubscribe. Ne touche pas au channel Supabase.
 *
 * Pousse immédiatement l'état courant (utile au mount du sidecar pour
 * afficher la liste sans attendre le prochain event).
 */
export function subscribePresenceList(listener: StateListener): () => void {
  if (!singleton) {
    // Pas encore joint (utilisateur pas authentifié / MainLayout pas monté)
    listener({});
    return () => {};
  }
  singleton.listeners.add(listener);
  // Push initial state
  listener(singleton.channel.presenceState() as PresenceState);
  return () => {
    if (singleton) singleton.listeners.delete(listener);
  };
}

export { PRESENCE_CHANNEL_NAME };
